import { useRef } from 'react'

import { Box, Card, Section } from '@factorialco/deprecated-design-system'
import { CustomFieldRenderProps, F0Form, f0FormField, useF0Form } from '@factorialco/f0-react'
import {
  Input,
  RichTextEditor,
  RichTextEditorHandle,
} from '@factorialco/f0-react/dist/experimental'
import { z } from 'zod'

import { observer } from 'mobx-react'

import TextEditor from 'modules/core/components/TextEditor'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import useRichTextEditor from 'modules/core/hooks/useRichTextEditor'
import i18n from 'modules/core/lib/i18n'

import { AtsMessageVariablesSelector } from 'modules/ats/components/AtsMessageVariablesSelector'
import type GDPRDataRetention from 'modules/ats/store/models/GDPRDataRetention'

interface Props {
  currentRetention: GDPRDataRetention
}

const EmailConsentRenewal = ({ currentRetention }: Props) => {
  const { canUseRichTextEditor, loading: richTextEditorLoading, editorConfig } = useRichTextEditor()
  const richTextEditorRef = useRef<RichTextEditorHandle>(null)
  const { formRef, setValue, getValues } = useF0Form()

  const subjectPlaceholder = i18n.t(
    'ats.settings.candidate_data.data_retention_request_email.text_editor.title_placeholder'
  )

  const bodyPlaceholder = i18n.t(
    'ats.settings.candidate_data.data_retention_request_email.text_editor.content_placeholder'
  )

  const toggleEnabled = currentRetention.attributes.consent_renewal_email_enabled ?? false

  const onSelectVariable = (variable: string) => {
    const currentBody = String(getValues().consentRenewalEmailBody ?? '')
    const content = `${currentBody} ${variable}`
    setValue('consentRenewalEmailBody', content)
    if (canUseRichTextEditor && richTextEditorRef.current) {
      richTextEditorRef.current.setContent(content)
    }
  }

  const { performAction: saveToggleData } = useAsyncAction(async (value: boolean) => {
    if (value) {
      // The backend rejects enabling the renewal email if subject/body are blank.
      // Fall back to the model attribute or the placeholder (same chain the form
      // uses for defaultValues) so we never POST blanks when toggling on.
      await currentRetention?.save({
        consent_renewal_email_enabled: true,
        consent_renewal_email_subject:
          currentRetention.attributes.consent_renewal_email_subject || subjectPlaceholder,
        consent_renewal_email_body:
          currentRetention.attributes.consent_renewal_email_body || bodyPlaceholder,
      })
    } else {
      await currentRetention?.save({ consent_renewal_email_enabled: false })
    }
  })

  const onToggle = (value: boolean) => {
    saveToggleData(value)
  }

  const handleSubmit = async (values: {
    consentRenewalEmailSubject: string
    consentRenewalEmailBody: string
  }) => {
    const updatedData = await currentRetention?.save({
      consent_renewal_email_enabled: toggleEnabled,
      consent_renewal_email_subject: values.consentRenewalEmailSubject,
      consent_renewal_email_body: values.consentRenewalEmailBody,
    })

    return { success: !!updatedData }
  }

  const schema = z.object({
    consentRenewalEmailSubject: f0FormField(
      toggleEnabled ? z.string().trim().min(1, i18n.t('common.errors.required')) : z.string(),
      {
        label: i18n.t('ats.settings.candidate_data.data_retention_request_email.text_editor.title'),
        fieldType: 'custom',
        render: (props: CustomFieldRenderProps<string>) => (
          <Box flexDirection="row" alignItems="flexEnd" justifyContent="spaceBetween" gap="s8">
            <Box flexGrow>
              <Input
                type="text"
                label={i18n.t(
                  'ats.settings.candidate_data.data_retention_request_email.text_editor.title'
                )}
                value={props.value}
                onChange={props.onChange}
                error={props.error}
              />
            </Box>
            <AtsMessageVariablesSelector onSelect={onSelectVariable} />
          </Box>
        ),
      }
    ),
    consentRenewalEmailBody: f0FormField(
      toggleEnabled
        ? z.string().refine((v) => v.replace(/<[^>]*>/g, '').trim().length > 0, {
            message: i18n.t('common.errors.required'),
          })
        : z.string(),
      {
        label: i18n.t('ats.manage_message_templates.template_form.body_label'),
        fieldType: 'custom',
        render: (props: CustomFieldRenderProps<string>) =>
          richTextEditorLoading ? (
            <RichTextEditor.Skeleton />
          ) : canUseRichTextEditor ? (
            <RichTextEditor
              ref={richTextEditorRef}
              {...editorConfig}
              title={i18n.t('ats.manage_message_templates.template_form.body_label')}
              onChange={(data) => props.onChange(data?.value ?? '')}
              placeholder={i18n.t(
                'ats.settings.candidate_data.data_retention_request_email.text_editor.content_placeholder'
              )}
              initialEditorState={{ content: props.value ?? '' }}
            />
          ) : (
            <TextEditor
              placeholder=""
              initialEditorState={{ data: props.value }}
              height="s200"
              onChange={(value) => props.onChange(typeof value === 'string' ? value : '')}
            />
          ),
      }
    ),
  })

  return (
    <Section.TwoThirds
      description={i18n.t('ats.settings.candidate_data.data_retention_request_email.description')}
      illustration="automations-default"
      title={i18n.t('ats.settings.candidate_data.data_retention_request_email.title')}
    >
      <Card>
        <Card.Title size="small">
          {i18n.t('ats.settings.candidate_data.data_retention_request_email.toggle.title')}
        </Card.Title>
        <Card.Description>
          {i18n.t('ats.settings.candidate_data.data_retention_request_email.toggle.description')}
        </Card.Description>
        <Card.ExpandableContent expanded={toggleEnabled} onExpandChange={onToggle}>
          <F0Form
            name="email-consent-renewal"
            schema={schema}
            formRef={formRef}
            defaultValues={{
              consentRenewalEmailSubject:
                currentRetention.attributes.consent_renewal_email_subject || subjectPlaceholder,
              consentRenewalEmailBody:
                currentRetention.attributes.consent_renewal_email_body || bodyPlaceholder,
            }}
            onSubmit={handleSubmit}
            submitConfig={{ type: 'default', label: i18n.t('common.submit') }}
          />
        </Card.ExpandableContent>
      </Card>
    </Section.TwoThirds>
  )
}

export default observer(EmailConsentRenewal)
